import React, { useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faXmark, faFile } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { toLocalDateString } from "@shared/renderDate";

interface FileItem {
  name: string;
  fullPath: string;
  size?: number;
  updated?: Date;
}

interface FilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: FileItem[];
  policyName: string;
  docId: string;
  onSelectFile: (file: FileItem, fixLinkage: boolean) => Promise<void>;
  isLoading?: boolean;
}

export const FilePickerDialog: React.FC<FilePickerDialogProps> = ({
  open,
  onOpenChange,
  files,
  policyName,
  docId,
  onSelectFile,
  isLoading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<FileItem | null>(null);
  const [fixLinkage, setFixLinkage] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (!selectedFile) return;
    
    setDownloading(true);
    try {
      await onSelectFile(selectedFile, fixLinkage);
      onOpenChange(false);
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setDownloading(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl! w-2xl! max-h-[90vh] bg-transparent shadow-none border-0 p-0 flex items-center justify-center"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="bg-card/95 backdrop-blur-xl rounded-xl w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/10 flex flex-col relative">
          {/* Close button */}
          <button
            onClick={() => onOpenChange(false)}
            disabled={downloading}
            className="absolute right-4 top-4 z-50 h-8 w-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Header */}
          <div className="p-8 pb-6 border-b border-white/10 bg-card text-center shrink-0">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-linear-to-br from-amber-500/10 to-amber-500/30 mb-4">
              <FontAwesomeIcon icon={faTriangleExclamation} className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">File Not Found</h2>
            <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
              The expected file for <strong className="text-foreground">"{policyName}"</strong> was not found.
              {files.length > 0 && (
                <> However, we found {files.length} file{files.length !== 1 ? 's' : ''} in the storage folder.</>
              )}
            </p>
          </div>

          {/* Content */}
          <div className="p-6 flex-1 min-h-0 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : files.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                No files found in storage folder.
              </div>
            ) : (
              <div className="rounded-xl border border-primary/10 bg-white/5 backdrop-blur-sm p-4 overflow-hidden">
                <p className="text-sm font-medium text-foreground mb-3">Select the correct file:</p>
                <ScrollArea className="max-h-[240px] overflow-hidden">
                  <div className="space-y-2 pr-4">
                    {files.map((file) => (
                      <div
                        key={file.fullPath}
                        className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedFile?.fullPath === file.fullPath
                            ? 'border-primary bg-primary/10 shadow-sm'
                            : 'border-white/10 hover:border-primary/30 hover:bg-white/5'
                        }`}
                        onClick={() => setSelectedFile(file)}
                      >
                        <div className="shrink-0 w-10 h-10 rounded-lg bg-linear-to-br from-blue-500/10 to-blue-500/30 flex items-center justify-center">
                          <FontAwesomeIcon icon={faFile} className="w-5 h-5 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground break-words">{file.name}</p>
                          {(file.size || file.updated) && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatFileSize(file.size)}
                              {file.size && file.updated && ' • '}
                              {file.updated && toLocalDateString(file.updated)}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Fix linkage option */}
            {files.length > 0 && (
              <div className="flex items-center gap-3 mt-4 p-4 rounded-xl border border-primary/10 bg-white/5">
                <Checkbox
                  id="fix-linkage"
                  checked={fixLinkage}
                  onCheckedChange={(checked) => setFixLinkage(checked === true)}
                />
                <label htmlFor="fix-linkage" className="text-sm text-muted-foreground cursor-pointer">
                  Update policy to use selected file (fixes future downloads)
                </label>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 pt-4 border-t border-white/10 bg-card/50 flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={downloading}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDownload}
              disabled={!selectedFile || downloading}
              className="w-full sm:w-auto bg-primary hover:bg-primary/90"
            >
              {downloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                'Download Selected'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilePickerDialog;
